import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import PortalLogo from '../PortalLogo';
import { LasersImage } from '../chrome/LasersBackground';
import Roller from './Roller';
import { RING, RING_STOPS, RING_ON, pillFill, pillOutline, pillRing, MONO } from './landingTokens';
import ringArt from '../../assets/img/portal-ring.png';

/**
 * The top of the landing page (handoff §Header, §1 Hero, §2 Statement, §3 Word reveal). The
 * scroll engine (useLandingScroll) drives everything tagged data-*; these components only lay
 * it out. The logo is the drawn lockup everywhere — never type.
 */

/** The page opens through a portal: a fixed black sheet with a hole the engine widens. */
export function IntroPortal() {
  return (
    <div data-intro="1" aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 200, pointerEvents: 'none', background: '#000' }}>
      <span data-intro-ring="1" style={{ position: 'absolute', left: '50%', top: '50%', width: '34vmin', aspectRatio: '1', transform: 'translate(-50%,-50%)', background: `url(${ringArt}) center / 100% 100% no-repeat` }} />
    </div>
  );
}

const navLink = { display: 'flex', alignItems: 'center', height: 40, padding: '0 14px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '.16em', color: '#BDBDBD', transition: 'color .2s, background-color .2s' };

export function LandingNav() {
  return (
    <header data-nav="1" data-testid="landing-nav" style={{ position: 'fixed', left: 0, right: 0, top: 0, zIndex: 50, background: '#000', transform: 'translateY(-100%)', transition: 'transform .45s cubic-bezier(.2,.7,.2,1)' }}>
      {/* The lockup is 150px at cap 24 and never shrinks; on a phone the two section links
          go (landing.css, under 480px) so SIGN IN stays on screen instead of past the edge. */}
      <div style={{ maxWidth: 1280, margin: '0 auto', height: 68, padding: '0 clamp(16px,4vw,40px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, boxSizing: 'border-box', minWidth: 0 }}>
        <a href="#top" aria-label="Portal home" className="pl-nolink" style={{ display: 'inline-flex', alignItems: 'center', color: '#fff', flex: 'none' }}><PortalLogo cap={24} /></a>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          <a href="#guests" className="pl-navlink" style={navLink}>GUESTS</a>
          <a href="#hosts" className="pl-navlink" style={navLink}>HOSTS</a>
          <Link to="/login" className="pl-ring-slide pl-pill-ring" style={{ display: 'flex', alignItems: 'center', height: 40, padding: '0 18px', border: '2px solid transparent', borderRadius: 999, background: RING_ON, backgroundSize: '100% 100%, 200% 100%', fontSize: 11, fontWeight: 800, letterSpacing: '.16em', color: '#fff', boxSizing: 'border-box' }}>SIGN IN</Link>
        </nav>
      </div>
      <div style={{ height: 2, background: RING }} />
    </header>
  );
}

function HeroBg() {
  const blob = (cls, style, grad) => <span key={cls} className={cls} style={{ position: 'absolute', borderRadius: '50%', filter: 'blur(60px)', background: grad, ...style }} />;
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
      {blob('pl-drift-a', { left: '-12%', top: '-20%', width: '62%', aspectRatio: '1' }, 'radial-gradient(circle, rgba(253,25,159,.55) 0%, rgba(253,25,159,0) 68%)')}
      {blob('pl-drift-b', { right: '-14%', top: '-10%', width: '58%', aspectRatio: '1' }, 'radial-gradient(circle, rgba(18,123,253,.55) 0%, rgba(18,123,253,0) 68%)')}
      {blob('pl-drift-c', { left: '22%', bottom: '-40%', width: '60%', aspectRatio: '1' }, 'radial-gradient(circle, rgba(140,110,255,.45) 0%, rgba(140,110,255,0) 66%)')}
      {blob('pl-drift-d', { right: '8%', bottom: '-30%', width: '34%', aspectRatio: '1' }, 'radial-gradient(circle, rgba(34,236,251,.4) 0%, rgba(34,236,251,0) 66%)')}
      <span data-hero-ring="1" className="pl-hero-spin" style={{ position: 'absolute', left: '50%', top: '50%', width: 'min(1100px,160vw)', aspectRatio: '1', transform: 'translate(-50%,-50%)', backgroundImage: `url(${ringArt})`, backgroundSize: '100% 100%', opacity: 0.32, mixBlendMode: 'screen' }} />
      <span style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,.18) 1px, transparent 1.2px)', backgroundSize: '22px 22px', WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 30%, #000 85%)', maskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 30%, #000 85%)' }} />
      <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: RING }} />
    </div>
  );
}

/** The 72px cue under the hero: a magenta pulse, the ring, a bobbing arrow. */
function CueRing() {
  return (
    <span style={{ position: 'relative', display: 'block', width: 72, height: 72 }}>
      <span className="pl-pulse" style={{ position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: '0 0 0 2px rgba(255,0,170,.7)' }} />
      <span className="pl-spin-fast" style={{ position: 'absolute', inset: 0, background: `url(${ringArt}) center / 100% 100% no-repeat` }} />
      <span style={{ position: 'absolute', inset: '9%', borderRadius: '50%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg className="pl-cue" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="square"><path d="M12 4v15M5 12l7 7 7-7" /></svg>
      </span>
    </span>
  );
}

export function HeroSection({ heroCap, onStepThrough }) {
  return (
    <section id="top" data-sx="hero" data-testid="hero" style={{ position: 'relative', zIndex: 2, height: '170vh', color: '#fff', pointerEvents: 'none' }}>
      <div data-hero-pin="1" style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', isolation: 'isolate', background: '#000', pointerEvents: 'auto' }}>
        <HeroBg />
        <div data-hero-content="1" style={{ position: 'relative', zIndex: 1, willChange: 'transform, opacity', maxWidth: 1280, margin: '0 auto', height: '100vh', minHeight: 440, padding: 'clamp(20px,6vh,96px) clamp(20px,5vw,64px) clamp(110px,16vh,150px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'clamp(16px,3.5vh,44px)', textAlign: 'center' }}>
          <div style={{ color: '#fff', lineHeight: 0, padding: `${Math.round(heroCap * 0.4)}px 0` }}><PortalLogo cap={heroCap} /></div>
          <h1 style={{ margin: 0, fontSize: 'min(clamp(26px,7vw,88px), 11vh, 8vw)', fontWeight: 800, letterSpacing: '.04em', lineHeight: 1, whiteSpace: 'nowrap' }}>FIND YOUR <Roller /></h1>
          {/* FIND and HOST share a line, SIGN IN sits under them (phones size the pills down, landing.css). */}
          <div className="pl-hero-ctas" data-testid="hero-ctas" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: '100%' }}>
            <div className="pl-hero-row" style={{ display: 'flex', justifyContent: 'center', gap: 12, maxWidth: '100%' }}>
              <Link to="/featured" className="pl-pill-fill" style={pillFill} data-testid="hero-find">FIND YOUR PARTY</Link>
              <a href="#hosts" className="pl-pill-outline" style={pillOutline} data-testid="hero-host">HOST AN EVENT</a>
            </div>
            <Link to="/login" className="pl-pill-ring" style={pillRing} data-testid="hero-signin">SIGN IN</Link>
          </div>
          <a href="#step" onClick={onStepThrough} aria-label="Step through the portal" className="pl-nolink" style={{ position: 'absolute', left: '50%', bottom: 'clamp(14px,4vh,40px)', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: '#fff' }}>
            <CueRing />
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.32em' }}>STEP THROUGH</span>
          </a>
        </div>
      </div>
    </section>
  );
}

/** "HUMAN." as outlines traced with the flowing ring gradient, with a white glint running along them. */
function HumanWord({ still }) {
  const id = `plh-${useId().replace(/:/g, '')}`;
  const letters = ['H', 'U', 'M', 'A', 'N', '.'].map((ch, i) => ({ ch, x: 72 + i * 64 }));
  const stops = [...RING_STOPS, RING_STOPS[0]];
  const text = { fontFamily: MONO, fontWeight: 800, fontSize: '100px' };
  return (
    <svg viewBox="0 0 460 104" width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="460" y2="0" spreadMethod="repeat">
          {stops.map((c, i) => <stop key={i} offset={i / (stops.length - 1)} stopColor={c} />)}
          {!still && <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="460 0" dur="4s" repeatCount="indefinite" />}
        </linearGradient>
      </defs>
      <g style={text}>{letters.map((l) => <text key={l.x} x={l.x} y={88} fill="none" stroke={`url(#${id})`} strokeWidth="2.6" strokeLinejoin="round">{l.ch}</text>)}</g>
      {!still && (
        <g style={text}>
          {letters.map((l) => (
            <text key={l.x} x={l.x} y={88} fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="22 420" opacity="0.9">
              {l.ch}
              <animate attributeName="stroke-dashoffset" from="442" to="0" dur="3.2s" repeatCount="indefinite" />
            </text>
          ))}
        </g>
      )}
    </svg>
  );
}

export function StatementSection({ still = false }) {
  return (
    <section id="step" data-sx="portal" data-testid="statement" style={{ position: 'relative', zIndex: 1, height: '290vh', marginTop: '-170vh', background: '#000' }}>
      <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
        <div data-sx-clip="1" style={{ position: 'absolute', inset: 0, overflow: 'hidden', clipPath: 'circle(13vmin at 50% 50%)', willChange: 'clip-path' }}>
          {/* The same static lasers as the app background, under the original vignette. */}
          <LasersImage />
          <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 50% at 50% 48%, rgba(0,0,0,.55), rgba(0,0,0,0) 75%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', inset: 0, padding: '0 clamp(20px,5vw,64px)', justifyContent: 'center', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(12px,2vw,20px)', textAlign: 'center' }}>
            <span style={{ fontSize: 'clamp(40px,8vw,104px)', fontWeight: 800, letterSpacing: '.04em', paddingLeft: '.04em', lineHeight: 1 }}>TO PARTY</span>
            <span style={{ fontSize: 'clamp(12px,1.4vw,16px)', fontWeight: 700, letterSpacing: '.6em', color: '#8A8A8A', paddingLeft: '.6em' }}>IS TO BE</span>
            <span role="img" aria-label="HUMAN." style={{ display: 'block', width: 'min(92vw, calc(clamp(40px,8vw,104px) * 4.6))', aspectRatio: '460 / 104', marginTop: '.1em' }}><HumanWord still={still} /></span>
          </div>
        </div>
        <span data-sx-ring="1" aria-hidden="true" style={{ position: 'absolute', left: '50%', top: '50%', width: '30vmin', aspectRatio: '1', transform: 'translate(-50%,-50%)', background: `url(${ringArt}) center / 100% 100% no-repeat`, pointerEvents: 'none', willChange: 'transform, width, opacity' }} />
        <span data-sx-hint="1" style={{ position: 'absolute', left: '50%', bottom: '6vh', transform: 'translateX(-50%)', fontSize: 10, fontWeight: 700, letterSpacing: '.3em', color: '#8A8A8A', whiteSpace: 'nowrap' }}>SCROLL TO STEP THROUGH</span>
      </div>
    </section>
  );
}

const REVEAL_WORDS = 'FIND YOUR PARTY. FIND YOUR PEOPLE. FIND YOUR PERSON.'.split(' ');

export function WordsSection() {
  return (
    <section data-sx="words" data-testid="words" style={{ position: 'relative', height: '125vh', background: '#000', color: '#fff' }}>
      <div style={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 clamp(20px,6vw,96px)', boxSizing: 'border-box' }}>
        <p style={{ margin: 0, maxWidth: 1100, fontSize: 'min(clamp(34px,6vw,92px), calc((100vw - 52px) / 10.9))', fontWeight: 800, lineHeight: 1.06, letterSpacing: '.02em', textAlign: 'center', whiteSpace: 'nowrap' }}>
          {REVEAL_WORDS.map((w, i) => (
            <React.Fragment key={i}>
              <span data-w={i} data-hot={/^P/.test(w) ? '1' : ''} style={{ color: '#262626', transition: 'color .25s' }}>{w}</span>
              {i < REVEAL_WORDS.length - 1 ? (/\.$/.test(w) ? <br /> : ' ') : null}
            </React.Fragment>
          ))}
        </p>
      </div>
    </section>
  );
}
